import { memo, useCallback, useMemo, useState } from 'react'
import { Check, FlaskConical, RadioTower } from 'lucide-react'
import type { LiveSourceConfig } from '../../lib/settingsApi.js'
import {
  deinterlaceStep,
  describeLiveDeinterlace,
  normalizeLiveDeinterlace,
  type DeinterlaceFilter,
  type DeinterlaceMode,
  type DeinterlaceParity,
  type DeinterlaceRate,
  type LiveDeinterlace,
} from '../../lib/liveDeinterlace.js'

interface Option<T extends string> { value: T; label: string; hint?: string }

const MODE_OPTIONS: Option<DeinterlaceMode>[] = [
  { value: 'AUTO', label: 'Auto', hint: 'CasparCG ne traite que les images que le flux déclare entrelacées.' },
  { value: 'FORCE', label: 'Forcé', hint: 'Toutes les images sont désentrelacées, même un flux qui se dit progressif.' },
  { value: 'OFF', label: 'Désactivé', hint: 'Aucune image n’est désentrelacée : pour une source progressive annoncée entrelacée, que CasparCG adoucirait pour rien. Une source vraiment entrelacée garde ses peignes.' },
]
const FILTER_OPTIONS: Option<DeinterlaceFilter>[] = [
  { value: 'BWDIF', label: 'bwdif', hint: 'recommandé' },
  { value: 'YADIF', label: 'yadif', hint: 'plus léger' },
]
const RATE_OPTIONS: Option<DeinterlaceRate>[] = [
  { value: 'FIELD', label: '50 i/s', hint: 'une image par trame, mouvement fluide' },
  { value: 'FRAME', label: '25 i/s', hint: 'une image par paire de trames' },
]
const PARITY_OPTIONS: Option<DeinterlaceParity>[] = [
  { value: 'AUTO', label: 'Auto', hint: 'celui annoncé par le flux' },
  { value: 'TFF', label: 'Haute d’abord', hint: 'trame haute en premier (TFF) — si le mouvement saccade en Auto' },
  { value: 'BFF', label: 'Basse d’abord', hint: 'trame basse en premier (BFF) — si le mouvement saccade en Auto' },
]

function Segmented<T extends string>({ label, name, value, options, disabled, onChange }: {
  label:    string
  name:     string
  value:    T
  options:  Option<T>[]
  disabled: boolean
  onChange: (value: T) => void
}) {
  const current = options.find(option => option.value === value)
  return (
    <div className="tc-setting">
      <span className="tc-setting__label" id={`${name}-label`}>{label}</span>
      <div className="tc-segmented" role="radiogroup" aria-labelledby={`${name}-label`}>
        {options.map(option => (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={option.value === value}
            className={`tc-segmented__option${option.value === value ? ' is-active' : ''}`}
            disabled={disabled}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
      {current?.hint && <span className="tc-caption">{current.hint}</span>}
    </div>
  )
}

export interface LiveSourceImageSettingsProps {
  source:  LiveSourceConfig
  /** Réglage de la source sur le canal 2 ; `undefined` si elle n'y est pas. */
  onCh2:   LiveDeinterlace | null | undefined
  busy:    boolean
  onTry:   (draft: LiveDeinterlace) => void
  onSave:  (draft: LiveDeinterlace) => void
}

/**
 * Réglage d'image d'une source Internet : on l'essaie sur le canal 2, puis on
 * l'enregistre pour l'antenne. « Enregistrer » ne s'ouvre que pour le réglage
 * qui tourne réellement sur le canal 2 (`deinterlaceStep`). Le parent le
 * monte avec `key={source.id}` : une autre source repart de son propre réglage.
 */
export const LiveSourceImageSettings = memo(function LiveSourceImageSettings({
  source, onCh2, busy, onTry, onSave,
}: LiveSourceImageSettingsProps) {
  const saved = useMemo(() => normalizeLiveDeinterlace(source.deinterlace), [source.deinterlace])
  const [draft, setDraft] = useState<LiveDeinterlace>(() => normalizeLiveDeinterlace(onCh2 ?? source.deinterlace))

  const update = useCallback(<K extends keyof LiveDeinterlace>(key: K, value: LiveDeinterlace[K]) => {
    setDraft(previous => ({ ...previous, [key]: value }))
  }, [])

  if (source.sourceType === 'DECKLINK') {
    return (
      <div className="tc-player__aside-block">
        <span className="tc-eyebrow">Image de la source</span>
        <p className="tc-caption">
          Entrée DeckLink : réglé par la carte, pas ici. Un signal progressif (1080p50) n’est jamais désentrelacé ;
          un signal entrelacé (1080i50) l’est toujours, dans l’ordre des trames annoncé, car le canal est en 50p.
        </p>
      </div>
    )
  }

  const step = deinterlaceStep(draft, saved, onCh2 === undefined ? undefined : onCh2)
  const forced = draft.mode === 'FORCE'

  return (
    <div className="tc-player__aside-block tc-image-settings">
      <span className="tc-eyebrow">Image de la source · désentrelacement</span>
      <Segmented label="Mode" name={`deint-mode-${source.id}`} value={draft.mode} options={MODE_OPTIONS} disabled={busy} onChange={value => update('mode', value)} />
      {forced && (
        <>
          <Segmented label="Filtre" name={`deint-filter-${source.id}`} value={draft.filter} options={FILTER_OPTIONS} disabled={busy} onChange={value => update('filter', value)} />
          <Segmented label="Cadence" name={`deint-rate-${source.id}`} value={draft.rate} options={RATE_OPTIONS} disabled={busy} onChange={value => update('rate', value)} />
          <Segmented label="Ordre des trames" name={`deint-parity-${source.id}`} value={draft.parity} options={PARITY_OPTIONS} disabled={busy} onChange={value => update('parity', value)} />
        </>
      )}

      <dl className="tc-image-settings__state">
        <div><dt><FlaskConical size={12} aria-hidden="true" />Canal 2</dt><dd className="tc-data">{onCh2 === undefined ? 'source non envoyée' : describeLiveDeinterlace(onCh2)}</dd></div>
        <div><dt><RadioTower size={12} aria-hidden="true" />Antenne</dt><dd className="tc-data">{describeLiveDeinterlace(saved)}</dd></div>
      </dl>

      <div className="tc-image-settings__actions">
        <button type="button" className="tp-button tc-send" disabled={busy || step !== 'TRY_ON_CH2'} onClick={() => onTry(draft)}>
          <FlaskConical size={13} aria-hidden="true" />Essayer sur CH2
        </button>
        <button type="button" className="tp-button tp-button--primary" disabled={busy || step !== 'READY_TO_SAVE'} onClick={() => onSave(draft)}>
          <Check size={13} aria-hidden="true" />Enregistrer pour l’antenne
        </button>
      </div>
      <p className="tc-caption" role="status">
        {step === 'UNCHANGED' && 'C’est le réglage de l’antenne.'}
        {step === 'TRY_ON_CH2' && 'Essayez d’abord ce réglage sur le canal 2 : l’antenne n’accepte qu’un réglage vu à l’image.'}
        {step === 'READY_TO_SAVE' && 'Vu sur le canal 2. S’appliquera à la prochaine prise d’antenne de cette source ; un direct déjà à l’antenne garde son réglage.'}
      </p>
    </div>
  )
})
